import { useContext } from "react";
import {
  Typography,
  useTheme,
  Box,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
} from "@mui/material";

import { centsToLocalString } from "../shared/money";
import { AppContext } from "../contextProviders/AppContext";
import { translation } from "./Dictionary";
import { getServiceDescription } from "../utilities";
import SelectedProductCard from "./SelectedProductCard";
import ServiceImage from "./AdditionalServiceImage";
import { calculateVATofPrice } from "./OrderSummary";

export default function PriceSummary() {
  const theme = useTheme();
  const { cart, selectedAdditionalServices, cartTotalValue, language } =
    useContext(AppContext);
  const imageSize = 50;
  const orderVatAmounts = calculateVATofPrice(cart, selectedAdditionalServices);

  return (
    <Stack
      sx={{
        flex: 1,
        borderRadius: "10px",
      }}
      spacing={1}
    >
      <Box>
        <Typography variant="h5" sx={{ color: theme.palette.primary.mainText }}>
          {translation.get(language, "Price summary")}
        </Typography>
      </Box>
      <Stack>
        <List>
          {cart &&
            cart.map((offer) => {
              return (
                <SelectedProductCard
                  offer={offer}
                  imageSize={imageSize}
                  key={offer?.vehicle?.id}
                />
              );
            })}

          {selectedAdditionalServices?.map((item) => {
            if (item.quantity === 0) return null;

            return (
              <ListItem key={item.id}>
                <ListItemAvatar>
                  <ServiceImage service={item} isAvatar imageSize={imageSize} />
                </ListItemAvatar>
                <ListItemText
                  disableTypography
                  primary={<ServiceDescription service={item} />}
                  secondary={
                    item.quantity > 1 && (
                      <Typography>
                        {item.quantity} {translation.get(language, "pcs text")}
                      </Typography>
                    )
                  }
                />

                <ListItemSecondaryAction
                  align="right"
                  style={{
                    padding: 0,
                    width: 80,
                    borderBottomWidth: 0,
                  }}
                >
                  <Typography
                    variant="body1"
                    style={{ color: theme.palette.primary.mainText }}
                  >
                    {`${centsToLocalString(item.offerPrice, true)}`}
                  </Typography>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      </Stack>
      <Box style={{ marginTop: 20 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems={"center"}
        >
          <Typography
            variant="h5"
            style={{ color: theme.palette.primary.mainText }}
          >
            {translation.get(language, "Total text")}
          </Typography>
          <Stack>
            <Typography
              sx={{
                color: theme.palette.primary.mainText,
                fontWeight: 550,
                fontSize: "18px",
              }}
              align="right"
            >
              {centsToLocalString(cartTotalValue, true)}
            </Typography>
            {orderVatAmounts &&
              orderVatAmounts.map((vat) => {
                return (
                  <Typography
                    variant="subtitle2"
                    align="right"
                    key={Object.keys(vat)}
                    style={{
                      color: theme.palette.primary.mainText,
                    }}
                  >
                    {`(${translation.get(language, "Inc vat")} ${Object.keys(
                      vat
                    )}%. ${centsToLocalString(Object.values(vat)[0], true)}) `}
                  </Typography>
                );
              })}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}

const ServiceDescription = ({ service }) => {
  const { language } = useContext(AppContext);
  const description = getServiceDescription(service, language);
  const theme = useTheme();

  return (
    <Typography
      variant="body1"
      sx={{
        color: theme.palette.primary.mainText,
        fontSize: "16px",
      }}
    >
      {description}
    </Typography>
  );
};
